import React from 'react';
import { reportClientError } from '../api';
import './ErrorBoundary.css';

// After a deploy, a page opened before it asks for code files that no longer exist. Reloading
// fetches the new ones; at most once a minute, so it can't loop if that isn't the cause.
const RELOADED_KEY = 'pickblink-reloaded-for-update';
const isStaleCode = (error) => /Loading (CSS )?chunk \d+ failed|ChunkLoadError/i.test(`${error?.name} ${error?.message}`);

const reloadOnce = () => {
  try {
    if (Date.now() - Number(sessionStorage.getItem(RELOADED_KEY) || 0) < 60000) return false;
    sessionStorage.setItem(RELOADED_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
};

// A crash anywhere in the app shows this instead of a blank page, and is reported so it can be
// fixed (POST /api/client-errors, emailed by the backend)
export class ErrorBoundary extends React.Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    if (isStaleCode(error) && reloadOnce()) return;
    reportClientError(error, info?.componentStack);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <main className="crash">
        <div className="crash-card">
          <h1>Something went wrong</h1>
          <p>This page hit a problem. It has been reported, and reloading usually fixes it. Your orders and storage are safe.</p>
          <div className="crash-actions">
            <button type="button" className="crash-primary" onClick={() => window.location.reload()}>Reload</button>
            <a href="/">Go to the dashboard</a>
          </div>
        </div>
      </main>
    );
  }
}

// Script errors outside React (event handlers, timers). Browser noise is left out.
export function reportUncaughtErrors() {
  window.addEventListener('error', (event) => {
    const message = event.error?.message || event.message || '';
    if (!message || /^Script error\.?$/.test(message) || /ResizeObserver loop/.test(message)) return;
    reportClientError(event.error || new Error(message));
  });
}
